import { useState, useRef, useCallback, useEffect } from 'react';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { View } from 'foundations-components/transitional/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { Link } from 'foundations-components/transitional/components/Link';
import { useTheme } from 'foundations-theming/utils';
import { LinkIcon } from 'foundations-assets/icons/components/LinkIcon';
import { useIsPillBorderRadius } from '../../hooks/useIsPillBorderRadius';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function SiteMenuItem({
  item,
  level,
  insideSection,
  navLinkWrapperStyle,
  navLinkWrapperHoverBg,
  popoverTextStyle
}) {
  var _item$linkTarget;
  const hasChildren = item.children && item.children.length > 0;
  const isSectionHeader = !item.url && hasChildren;
  const effectiveLevel = isSectionHeader && insideSection ? level + 1 : level;
  const INDENT_TOKENS = [undefined, '300', '600'];
  const indentToken = INDENT_TOKENS[Math.min(effectiveLevel, INDENT_TOKENS.length - 1)];
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(View, {
      paddingInlineStart: indentToken,
      children: isSectionHeader ? /*#__PURE__*/_jsx("span", {
        style: Object.assign({}, popoverTextStyle, {
          display: 'block',
          paddingBlock: '4px',
          paddingInlineStart: '8px',
          paddingInlineEnd: '8px',
          fontSize: '11px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          opacity: 0.6,
          marginBlockStart: '8px'
        }),
        children: item.label
      }) : /*#__PURE__*/_jsx("div", {
        style: navLinkWrapperStyle,
        onMouseEnter: e => {
          if (!item.isNonLinkingNode) {
            e.currentTarget.style.backgroundColor = navLinkWrapperHoverBg;
          }
        },
        onMouseLeave: e => {
          e.currentTarget.style.backgroundColor = 'transparent';
        },
        children: item.isNonLinkingNode || !item.url ? /*#__PURE__*/_jsx("span", {
          style: Object.assign({}, popoverTextStyle, {
            display: 'block',
            paddingBlock: '4px',
            paddingInlineStart: '8px',
            paddingInlineEnd: '8px'
          }),
          children: item.label
        }) : /*#__PURE__*/_jsx(Link, {
          href: item.url,
          external: item.linkTarget === '_blank',
          target: (_item$linkTarget = item.linkTarget) !== null && _item$linkTarget !== void 0 ? _item$linkTarget : undefined,
          display: "block",
          paddingBlock: "100",
          paddingInline: "200",
          use: "unstyled",
          children: /*#__PURE__*/_jsx("span", {
            style: popoverTextStyle,
            children: item.label
          })
        })
      })
    }), hasChildren && item.children.map((child, index) => /*#__PURE__*/_jsx(SiteMenuItem, {
      item: child,
      level: effectiveLevel,
      insideSection: insideSection || isSectionHeader,
      navLinkWrapperStyle: navLinkWrapperStyle,
      navLinkWrapperHoverBg: navLinkWrapperHoverBg,
      popoverTextStyle: popoverTextStyle
    }, `${child.label}-${index}`))]
  });
}
export function SiteMenuNavItem({
  menuData
}) {
  var _theme$typography$bod, _theme$typography$bod2, _theme$typography$bod3, _theme$typography$bod4;
  const [open, setOpen] = useState(false);
  const closeTimeout = useRef(undefined);
  const theme = useTheme();
  const isPill = useIsPillBorderRadius();
  useEffect(() => () => clearTimeout(closeTimeout.current), []);
  const navLinkWrapperStyle = {
    borderRadius: theme.borderRadius[isPill ? '0' : '100'],
    transition: 'background-color 150ms'
  };
  const navLinkWrapperHoverBg = theme.color['fill-surface-default-hover'];
  const popoverTextStyle = {
    fontFamily: (_theme$typography$bod = theme.typography['body-100']) === null || _theme$typography$bod === void 0 ? void 0 : _theme$typography$bod.fontFamily,
    fontSize: (_theme$typography$bod2 = theme.typography['body-100']) === null || _theme$typography$bod2 === void 0 ? void 0 : _theme$typography$bod2.fontSize,
    fontWeight: (_theme$typography$bod3 = theme.typography['body-100']) === null || _theme$typography$bod3 === void 0 ? void 0 : _theme$typography$bod3.fontWeight,
    lineHeight: (_theme$typography$bod4 = theme.typography['body-100']) === null || _theme$typography$bod4 === void 0 ? void 0 : _theme$typography$bod4.lineHeight
  };
  const show = useCallback(() => {
    clearTimeout(closeTimeout.current);
    setOpen(true);
  }, []);
  const hide = useCallback(() => {
    closeTimeout.current = setTimeout(() => setOpen(false), 150);
  }, []);
  const popoverBody = /*#__PURE__*/_jsx("span", {
    style: popoverTextStyle,
    children: /*#__PURE__*/_jsx(View, {
      onMouseEnter: show,
      onMouseLeave: hide,
      padding: "200",
      minInlineSize: "200px",
      children: menuData.items.map((item, index) => /*#__PURE__*/_jsx(SiteMenuItem, {
        item: item,
        level: 0,
        insideSection: false,
        navLinkWrapperStyle: navLinkWrapperStyle,
        navLinkWrapperHoverBg: navLinkWrapperHoverBg,
        popoverTextStyle: popoverTextStyle
      }, `${item.label}-${index}`))
    })
  });
  return /*#__PURE__*/_jsx(View, {
    onMouseEnter: show,
    onMouseLeave: hide,
    "data-test-id": "portals-side-nav-site-menu",
    children: /*#__PURE__*/_jsx(Popover, {
      open: open,
      onOpenChange: ({
        target: {
          value
        }
      }) => setOpen(value),
      content: popoverBody,
      placement: "right bottom",
      arrowSize: "none",
      closeOnOutsideClick: false,
      children: /*#__PURE__*/_jsx(Flex, {
        borderRadius: isPill ? '0' : '100',
        alignItems: "center",
        justifyContent: "center",
        padding: "300",
        backgroundColor: open ? 'fill-accent-neutral-subtle' : undefined,
        children: /*#__PURE__*/_jsx(LinkIcon, {
          size: "500"
        })
      })
    })
  });
}